import React, { useState, useEffect, useCallback } from 'react';
import {
    FaSync,
    FaCheckCircle,
    FaExclamationTriangle,
    FaClock,
    FaTimes,
    FaInfoCircle,
    FaBolt,
    FaShieldAlt,
} from 'react-icons/fa';
import { getOnboardingPurgatorySyncStatus } from '../../services/onboardingService';

const REVIEW_REASON_LABELS = {
    NO_EXISTING_EMPLOYEE_MATCH: 'No matching HA employee',
    MISSING_AGENCY_DATA: 'Missing agency information',
    UNSUPPORTED_AGENCY: 'Unsupported agency',
    VALIDATION_FAILED: 'Candidate details need checking',
    CANDIDATE_PROCESSING_EXCEPTION: 'Candidate could not be processed',
};

const HourlySyncStatusBar = ({ serverID }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const loadSyncStatus = useCallback(async () => {
        if (!serverID) return;
        setLoading(true);
        setError(null);

        try {
            const res = await getOnboardingPurgatorySyncStatus(serverID);
            if (res?.success === true) {
                setData(res);
            } else {
                setError('Sync status unavailable.');
            }
        } catch (err) {
            console.error('Failed to load sync status:', err);
            setError(err?.message || 'Could not load sync status.');
        } finally {
            setLoading(false);
        }
    }, [serverID]);

    useEffect(() => {
        loadSyncStatus();
        // Periodic check every 30 seconds
        const timer = setInterval(() => {
            loadSyncStatus();
        }, 30000);
        return () => clearInterval(timer);
    }, [loadSyncStatus]);

    const state = data?.status || '';
    const available = data?.available === true;
    const isCompleted = state === 'completed' || state === 'completed_with_issues';
    const isFailed = state === 'failed' || state === 'interrupted';
    const isRunning = state === 'running';
    const needsReview = state === 'completed_with_issues';

    const statusLabel = {
        completed: 'Up to date',
        completed_with_issues: 'Completed · review items',
        running: 'Sync in progress',
        failed: 'Latest sync failed',
        interrupted: 'Sync interrupted',
    }[state] || 'Awaiting sync';

    const statusColor = isFailed
        ? '#c53030'
        : needsReview
            ? '#95631c'
            : isRunning
                ? '#2b6cb0'
                : '#176B64';

    const statusBg = isFailed
        ? '#fff5f5'
        : needsReview
            ? '#fffbeb'
            : isRunning
                ? '#ebf8ff'
                : '#E5F2EF';

    const reasons = (data?.reviewReasons && typeof data.reviewReasons === 'object') ? data.reviewReasons : {};

    return (
        <>
            {/* Status Bar Container */}
            <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #DEE8E6',
                padding: '16px 20px',
                marginBottom: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '12px',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            padding: '10px',
                            backgroundColor: '#EDF5F3',
                            borderRadius: '10px',
                            color: '#176B64',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <FaSync className={loading || isRunning ? 'fa-spin' : ''} style={{ fontSize: '16px' }} />
                        </div>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: '700', fontSize: '15px', color: '#203838' }}>
                                    Onboarding360 Sync
                                </span>
                                {available && (
                                    <span style={{
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        backgroundColor: statusBg,
                                        color: statusColor,
                                        border: `1px solid ${statusColor}33`,
                                    }}>
                                        {statusLabel}
                                    </span>
                                )}
                            </div>
                            <div style={{ fontSize: '12px', color: '#687D80', marginTop: '2px' }}>
                                {available
                                    ? `Last successful sync · ${data?.lastSuccessfulLabel || 'Not yet recorded'}`
                                    : loading
                                        ? 'Loading sync status…'
                                        : 'No sync recorded yet'}
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {available && (
                            <button
                                type="button"
                                onClick={() => setShowModal(true)}
                                style={{
                                    backgroundColor: '#edf2f7',
                                    color: '#176B64',
                                    border: '1px solid #cbd5e0',
                                    padding: '6px 14px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                }}
                            >
                                View Details
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={loadSyncStatus}
                            disabled={loading}
                            title="Refresh status — does not trigger a sync"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                backgroundColor: '#ffffff',
                                color: '#4a5568',
                                border: '1px solid #cbd5e0',
                                padding: '6px 10px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                cursor: loading ? 'not-allowed' : 'pointer',
                            }}
                        >
                            <FaSync className={loading ? 'fa-spin' : ''} />
                        </button>
                    </div>
                </div>

                {/* Metric Summary Badges */}
                {available && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                        {isCompleted && (
                            <>
                                <span style={{
                                    padding: '4px 10px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    backgroundColor: '#F3F7F6',
                                    color: '#176B64',
                                    border: '1px solid #d1e7e4',
                                    fontWeight: '600',
                                }}>
                                    <strong style={{ fontWeight: '800' }}>{data?.staged ?? 0}</strong> newly staged
                                </span>
                                <span style={{
                                    padding: '4px 10px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    backgroundColor: '#f8fafc',
                                    color: '#4a5568',
                                    border: '1px solid #e2e8f0',
                                    fontWeight: '600',
                                }}>
                                    <strong style={{ fontWeight: '800' }}>{data?.unchanged ?? 0}</strong> unchanged
                                </span>
                                {(data?.manualReviewCount ?? 0) > 0 && (
                                    <span style={{
                                        padding: '4px 10px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        backgroundColor: '#FFF7E9',
                                        color: '#80561B',
                                        border: '1px solid #fed7aa',
                                        fontWeight: '700',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}>
                                        <FaExclamationTriangle style={{ fontSize: '11px' }} />
                                        <strong>{data?.manualReviewCount}</strong> source checks
                                    </span>
                                )}
                            </>
                        )}
                        {(data?.blockedIdentityConflicts ?? 0) > 0 && (
                            <span style={{
                                padding: '4px 10px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                backgroundColor: '#fff5f5',
                                color: '#c53030',
                                border: '1px solid #feb2b2',
                                fontWeight: '700',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                            }}>
                                <FaShieldAlt style={{ fontSize: '11px' }} />
                                <strong>{data?.blockedIdentityConflicts}</strong> identity conflicts
                            </span>
                        )}
                    </div>
                )}

                {error && (
                    <div style={{ fontSize: '12px', color: '#c53030', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FaExclamationTriangle />
                        <span>{data ? 'Showing saved status. ' : ''}{error}</span>
                    </div>
                )}
            </div>

            {/* Sync Details Modal */}
            {showModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    padding: '20px',
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '16px',
                        maxWidth: '620px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    }}>
                        {/* Modal Header */}
                        <div style={{
                            padding: '20px 24px',
                            borderBottom: '1px solid #edf2f7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    padding: '10px',
                                    borderRadius: '10px',
                                    backgroundColor: '#E7F2EF',
                                    color: '#246D67',
                                }}>
                                    <FaSync style={{ fontSize: '18px' }} />
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#203838' }}>
                                        Sync Result
                                    </h3>
                                    <div style={{ fontSize: '12px', color: '#738584' }}>
                                        Onboarding360 Integration Staging
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#a0aec0',
                                    fontSize: '18px',
                                    cursor: 'pointer',
                                    padding: '4px',
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div style={{ padding: '24px' }}>
                            {/* Summary Banner */}
                            <div style={{
                                padding: '16px',
                                borderRadius: '10px',
                                backgroundColor: `${statusColor}11`,
                                border: `1px solid ${statusColor}33`,
                                marginBottom: '20px',
                            }}>
                                <div style={{ fontWeight: '700', fontSize: '14px', color: statusColor, marginBottom: '4px' }}>
                                    {isFailed
                                        ? 'Sync needs attention'
                                        : isRunning
                                            ? 'Sync is in progress'
                                            : needsReview
                                                ? 'Completed with review items'
                                                : isCompleted
                                                    ? 'Sync completed'
                                                    : 'No completed result yet'}
                                </div>
                                <div style={{ fontSize: '12.5px', color: '#4a5568', lineHeight: 1.5 }}>
                                    {isFailed
                                        ? 'This attempt did not finish successfully. Review the sync log before trying again.'
                                        : isRunning
                                            ? 'The latest candidate information is being checked. Close this window and refresh the status shortly.'
                                            : isCompleted
                                                ? 'Incoming candidate information has been checked. Employee profiles have not been changed.'
                                                : 'Run a sync to prepare incoming changes for review.'}
                                </div>
                            </div>

                            {/* 3 Metrics Cards */}
                            {isCompleted && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
                                    <div style={{ padding: '14px', borderRadius: '10px', backgroundColor: '#F0F9F8', border: '1px solid #BFE3DE' }}>
                                        <div style={{ fontSize: '24px', fontWeight: '800', color: '#176B64' }}>
                                            {data?.staged ?? 0}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#687D80', marginTop: '2px' }}>
                                            Newly staged
                                        </div>
                                    </div>

                                    <div style={{ padding: '14px', borderRadius: '10px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                        <div style={{ fontSize: '24px', fontWeight: '800', color: '#2d3748' }}>
                                            {data?.unchanged ?? 0}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#687D80', marginTop: '2px' }}>
                                            Unchanged
                                        </div>
                                    </div>

                                    <div style={{ padding: '14px', borderRadius: '10px', backgroundColor: '#FFF7E9', border: '1px solid #fed7aa' }}>
                                        <div style={{ fontSize: '24px', fontWeight: '800', color: '#95631C' }}>
                                            {data?.manualReviewCount ?? 0}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#80561B', marginTop: '2px' }}>
                                            Source checks
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Details List */}
                            <div style={{
                                backgroundColor: '#f8fafc',
                                borderRadius: '10px',
                                border: '1px solid #e2e8f0',
                                padding: '14px 18px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                                marginBottom: '20px',
                                fontSize: '13px',
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: '#687D80', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <FaCheckCircle style={{ color: '#176B64' }} /> Last successful sync:
                                    </span>
                                    <strong style={{ color: '#2d3748' }}>{data?.lastSuccessfulLabel || 'Not yet recorded'}</strong>
                                </div>

                                <div style={{ borderTop: '1px solid #edf2f7', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: '#687D80', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <FaClock style={{ color: '#4a5568' }} /> Latest attempt started:
                                    </span>
                                    <strong style={{ color: '#2d3748' }}>{data?.latestStartedLabel || 'Not available'}</strong>
                                </div>

                                <div style={{ borderTop: '1px solid #edf2f7', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: '#687D80', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <FaBolt style={{ color: '#d97706' }} /> Triggered by:
                                    </span>
                                    <strong style={{ color: '#2d3748' }}>
                                        {data?.trigger === 'scheduled'
                                            ? 'Hourly schedule'
                                            : data?.trigger === 'manual'
                                                ? 'An administrator'
                                                : 'Not available'}
                                    </strong>
                                </div>
                            </div>

                            {/* Review Reasons Breakdown */}
                            {Object.keys(reasons).length > 0 && (
                                <div style={{ marginBottom: '20px' }}>
                                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#2d3748', marginBottom: '8px' }}>
                                        Source Check Reasons
                                    </div>
                                    <div style={{
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                    }}>
                                        {Object.entries(reasons).map(([code, count], idx) => (
                                            <div
                                                key={code}
                                                style={{
                                                    padding: '10px 14px',
                                                    backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8fafc',
                                                    borderBottom: idx < Object.keys(reasons).length - 1 ? '1px solid #edf2f7' : 'none',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    fontSize: '12.5px',
                                                }}
                                            >
                                                <span style={{ color: '#4a5568' }}>
                                                    {REVIEW_REASON_LABELS[code] || code.replace(/_/g, ' ')}
                                                </span>
                                                <span style={{
                                                    backgroundColor: '#edf2f7',
                                                    color: '#2d3748',
                                                    fontWeight: '700',
                                                    padding: '2px 8px',
                                                    borderRadius: '6px',
                                                    fontSize: '12px',
                                                }}>
                                                    {count}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Identity Conflicts Alert */}
                            {(data?.blockedIdentityConflicts ?? 0) > 0 && (
                                <div style={{
                                    padding: '12px 16px',
                                    borderRadius: '8px',
                                    backgroundColor: '#fffbeb',
                                    border: '1px solid #fed7aa',
                                    fontSize: '12.5px',
                                    color: '#95631C',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '8px',
                                }}>
                                    <FaExclamationTriangle style={{ marginTop: '2px' }} />
                                    <span>
                                        <strong>{data.blockedIdentityConflicts} records</strong> could not be staged because their account identities conflict across franchise systems.
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Modal Actions */}
                        <div style={{
                            padding: '16px 24px',
                            borderTop: '1px solid #edf2f7',
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}>
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                style={{
                                    backgroundColor: '#176B64',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '8px 20px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                }}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default HourlySyncStatusBar;
